// Form field primitives. `InputField` is a drop-in replacement for components/Input.
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "./cn";
import { sxToStyle } from "./sx";

export function Label({ children, optional, error, htmlFor, className, style }) {
  return (
    <label htmlFor={htmlFor} style={style} className={cn("mb-1 block text-[12.5px] font-medium", error ? "text-danger" : "text-text", className)}>
      {children}
      {optional && <span className="ml-1 font-normal text-faint">(optional)</span>}
    </label>
  );
}

export function FieldError({ children }) {
  if (!children || children === true) return null;
  return <p className="mt-1 text-[12px] text-danger">{children}</p>;
}

export const inputClass =
  "w-full rounded-lg border border-line bg-panel px-3 text-[13.5px] text-fg shadow-card outline-none transition-[border-color,box-shadow] placeholder:text-faint hover:border-line-strong focus:border-accent focus:ring-3 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-[#f4f4f6] disabled:text-muted";

/** Plain styled input (no label). */
export function Input({ className, invalid, ...props }) {
  return <input className={cn(inputClass, "h-10", invalid && "border-danger focus:border-danger focus:ring-danger/15", className)} {...props} />;
}

const adornmentClass = "flex shrink-0 items-center text-[13px] text-muted [&_.MuiInputAdornment-root]:m-0 [&_.MuiTypography-root]:text-[13px] [&_svg]:size-4 [&_svg]:text-faint";

/**
 * Drop-in replacement for components/Input `InputField`.
 * Supports: label, labelIcon, placeholder, register (react-hook-form; its handlers take precedence over
 * onChange/onBlur, as before), error, helperText, type, value, onChange, defaultValue, disabled,
 * multiline/rows/minRows, startAdornment/endAdornment, InputProps ({start,end}Adornment, inputProps,
 * readOnly), inputProps, inputStyle (sx for the field), style (sx for the wrapper), custom (sx for the
 * label), optional, onKeyDown, onBlur, onInput, id, step (defaults to "any"), inputRef, margin, dir,
 * readOnly. Accepted and ignored (MUI-only): size, variant, fullWidth, sx.
 */
export function InputField({
  label,
  labelIcon,
  placeholder,
  register,
  error,
  helperText,
  type,
  value,
  onChange,
  defaultValue,
  defautlValue,
  disabled,
  multiline,
  rows,
  minRows,
  startAdornment,
  endAdornment,
  InputProps,
  inputProps,
  inputStyle,
  style,
  custom,
  optional,
  onKeyDown,
  onBlur,
  onInput,
  id,
  step,
  inputRef,
  margin,
  dir,
  readOnly,
  required,
  className,
}) {
  const start = startAdornment ?? InputProps?.startAdornment;
  const end = endAdornment ?? InputProps?.endAdornment;
  const reg = register || {};
  const setRef = (el) => {
    if (typeof reg.ref === "function") reg.ref(el);
    if (typeof inputRef === "function") inputRef(el);
    else if (inputRef) inputRef.current = el;
  };
  const common = {
    id,
    dir,
    name: reg.name,
    ref: setRef,
    placeholder,
    disabled,
    required,
    readOnly: readOnly ?? InputProps?.readOnly,
    onChange: reg.onChange || onChange,
    onBlur: reg.onBlur || onBlur,
    onKeyDown,
    onInput,
    "aria-invalid": error ? true : undefined,
    ...(value !== undefined ? { value: value ?? "" } : {}),
    ...(defaultValue !== undefined || defautlValue !== undefined ? { defaultValue: defaultValue ?? defautlValue } : {}),
    ...InputProps?.inputProps,
    ...inputProps,
  };
  const invalid = Boolean(error);
  const fieldStyle = sxToStyle(inputStyle);
  const wrapperStyle = sxToStyle(style);
  const mb = margin === 0 || wrapperStyle?.margin !== undefined || wrapperStyle?.marginBottom !== undefined ? "" : "mb-4";
  const errorText = typeof error === "string" ? error : null;

  return (
    <div className={cn(mb, className)} style={wrapperStyle}>
      {label && (
        <Label htmlFor={id} optional={optional} error={invalid} style={sxToStyle(custom)}>
          {labelIcon && <img src={labelIcon} alt="" className="mr-1 inline size-[13px] align-[-1px]" />}
          {label}
        </Label>
      )}
      {multiline ? (
        <textarea
          rows={rows || minRows || 3}
          style={fieldStyle}
          className={cn(inputClass, "resize-y py-2.5", invalid && "border-danger focus:ring-danger/15")}
          {...common}
        />
      ) : start || end ? (
        <div
          style={fieldStyle}
          className={cn(
            inputClass,
            "flex h-10 items-center gap-2 focus-within:border-accent focus-within:ring-3 focus-within:ring-accent/20",
            disabled && "cursor-not-allowed bg-[#f4f4f6]",
            invalid && "border-danger focus-within:border-danger focus-within:ring-danger/15",
          )}
        >
          {start && <span className={adornmentClass}>{start}</span>}
          <input
            type={type || "text"}
            step={step ?? "any"}
            onWheel={(e) => type === "number" && e.currentTarget.blur()}
            className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[13.5px] text-fg outline-none placeholder:text-faint disabled:cursor-not-allowed disabled:text-muted"
            {...common}
          />
          {end && <span className={cn(adornmentClass, "-mr-1.5")}>{end}</span>}
        </div>
      ) : (
        <input
          type={type || "text"}
          step={step ?? "any"}
          onWheel={(e) => type === "number" && e.currentTarget.blur()}
          style={fieldStyle}
          className={cn(inputClass, "h-10", invalid && "border-danger focus:border-danger focus:ring-danger/15")}
          {...common}
        />
      )}
      {errorText ? <FieldError>{errorText}</FieldError> : helperText ? <p className="mt-1 text-[12px] text-muted">{helperText}</p> : null}
    </div>
  );
}

/** InputField with a show/hide toggle. Same props as InputField. */
export function PasswordField(props) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <InputField
      {...props}
      type={visible ? "text" : "password"}
      endAdornment={
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="grid size-7 place-items-center rounded-md text-faint hover:bg-black/[0.045] hover:text-fg"
        >
          <Icon className="size-4" />
        </button>
      }
    />
  );
}
